import {
  AfterViewInit,
  Component,
  computed,
  ContentChild,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  OnInit,
  Output,
  signal,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { CalendarBuilder, DateModel } from "@root/component/multi-calendar/multi-calendar.service";
import { head, last } from "lodash";
import { debounceTime, fromEvent } from "rxjs";
import { CalendarLayoutService } from "./calendar-layout.service";

@Component({
  selector: "host-calendar-layout",
  templateUrl: "./calendar-layout.component.html",
  styleUrls: ["./calendar-layout.component.scss"],
})
export class CalendarLayoutComponent implements OnInit, AfterViewInit {
  @ViewChild("layoutContainer") layoutContainer: ElementRef;
  @ContentChild("calendar") calendar: TemplateRef<any>;
  @ContentChild("availableCount") availableCount: TemplateRef<any>;
  @Output() onDatesChange = new EventEmitter<[string, string]>();
  @Output() onScrollToBottom = new EventEmitter<boolean>();
  cellWidth = 60;
  datesInViewCount = signal(30);
  cursor = signal(0);

  listOfAllDates: DateModel[] = this.cb.buildDefaultCalendar();

  listOfDatesInView = computed(() => {
    return this.listOfAllDates.slice(this.cursor(), this.cursor() + this.datesInViewCount());
  });

  firstCompleteDateInView = computed(() => {
    if (this.listOfDatesInView()?.[0]?.offsetStart * this.cellWidth < this.layoutContainer?.nativeElement?.scrollLeft) {
      return this.listOfDatesInView()?.[1]?.date;
    }
    return this.listOfDatesInView()?.[0]?.date;
  });

  trackDate = () => {};

  destroyRef = inject(DestroyRef);
  injector = inject(Injector);

  constructor(
    private cb: CalendarBuilder,
    private layoutSrv: CalendarLayoutService,
  ) {}

  ngOnInit() {}

  ngAfterViewInit(): void {
    this.calculateDatesInViewCount();

    /** Dom events */
    this.subscribeScrollEvent();
    this.subscribeResizeEvent();

    /** Data events */
    this.subscribeCursorChange();
    this.subscribeJumpEvent();

    this.scrollTo(365);
  }

  private subscribeScrollEvent() {
    fromEvent(this.layoutContainer.nativeElement, "scroll")
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe((event: Event) => {
        console.log("after scroll");
        const scrollLeft = Math.ceil((event.target as HTMLElement).scrollLeft);
        const cursor = Math.floor(scrollLeft / this.cellWidth);

        if (cursor !== this.cursor()) {
          // 左右滚动
          this.cursor.set(cursor);
        } else {
          // 上下滚动
          const target = event.target as HTMLElement;
          this.checkIfScrollToBottom(target);
        }
      });
  }

  private subscribeResizeEvent() {
    fromEvent(window, "resize")
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.calculateDatesInViewCount();
      });
  }

  private subscribeJumpEvent() {
    this.layoutSrv.jumpToDate$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((date) => {
      console.log("scrollTo jump: ", date);
      if (date === "L") {
        this.scrollByDistance(this.layoutContainer.nativeElement.offsetWidth - 200 - this.cellWidth);
      } else if (date === "R") {
        this.scrollByDistance(-(this.layoutContainer.nativeElement.offsetWidth - 200 - this.cellWidth));
      } else {
        this.scrollTo(this.cb.offsetStart(date));
      }

      // const index = this.listOfAllDates.findIndex((item) => item.date === date);
      // this.cursor.set(index);
    });
  }

  private subscribeCursorChange() {
    toObservable(this.cursor, { injector: this.injector }).subscribe(() => {
      console.log("cursor change");
      this.onDatesChange.emit([head(this.listOfDatesInView())?.date, last(this.listOfDatesInView())?.date]);
    });
  }

  private calculateDatesInViewCount() {
    const count = Math.ceil(this.layoutContainer.nativeElement.offsetWidth / this.cellWidth) + 1;
    console.log("dates in view count: ", count);
    this.datesInViewCount.set(count);
  }

  private checkIfScrollToBottom(target: HTMLElement) {
    if (target.clientHeight + target.scrollTop + 14 >= target.scrollHeight) {
      console.log("scroll to bottom: ", true);
      this.onScrollToBottom.emit(true);
    } else {
      console.log("scroll to bottom: ", false);
      this.onScrollToBottom.emit(false);
    }
  }

  scrollTo(cursor: number) {
    console.log("scrollTo: ", cursor);
    this.layoutContainer.nativeElement.scrollLeft = cursor * this.cellWidth;
  }

  scrollByDistance(distance: number) {
    const currentLeft = this.layoutContainer.nativeElement.scrollLeft;
    this.layoutContainer.nativeElement.scrollLeft = currentLeft - distance;
  }
}
